import React from 'react';
import { useInboundAcceptance } from '@contexts/operator/InboundAcceptanceContext';
import { Table, Tag, Space, Avatar, Typography, Button, Progress, Tooltip, Badge } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined, ExclamationCircleOutlined, SyncOutlined, InboxOutlined, FormOutlined } from '@ant-design/icons';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';

const { Text } = Typography;

interface AttributeDefinition {
  name: string;
  label: string;
  [key: string]: unknown;
}

interface ReceiptItemSummary {
  id?: string;
  [key: string]: unknown;
}

interface InboundItemRecord {
  id: string;
  product_name?: string;
  product_sku?: string;
  product_barcode?: string;
  product?: { image_url?: string | null; [key: string]: unknown } | null;
  received_at?: string | null;
  received_quantity?: number;
  scheduled_quantity?: number;
  batch?: string | null;
  expiration_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  receipt_items?: ReceiptItemSummary[];
  [key: string]: unknown;
}

const ItemsTable: React.FC = () => {
  const { inbound, scannedItem, openVerification, attributeDefinitions } = useInboundAcceptance();
  const { t } = useTranslation();

  const attributeLabelByName = React.useMemo(
    () => Object.fromEntries(((attributeDefinitions || []) as AttributeDefinition[]).map((def) => [def.name, def.label])),
    [attributeDefinitions]
  );

  const handleManualVerify = (record: InboundItemRecord) => {
    openVerification(record);
  };

  const columns: ColumnsType<InboundItemRecord> = [
    {
      title: t('common.status_label', { defaultValue: '狀態' }),
      key: 'status',
      width: 60,
      render: (_, record) => {
        const isReceived = !!record.received_at;

        if (!isReceived) {
          return <SyncOutlined spin={false} style={{ color: '#d9d9d9', fontSize: '18px' }} />;
        }
        if ((record.received_quantity ?? 0) >= (record.scheduled_quantity ?? 0)) {
          return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />;
        }
        return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '18px' }} />;
      },
    },
    {
      title: t('models.product.image', { defaultValue: '圖片' }),
      dataIndex: ['product', 'image_url'],
      key: 'image',
      width: 80,
      render: (url: string | null | undefined) => url ? <Avatar shape="square" size={64} src={url} icon={<InboxOutlined />} /> : null
    },
    {
      title: t('models.product.info', { defaultValue: '商品資訊' }),
      key: 'info',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.product_name}</Text>
          <Text type="secondary" copyable>{record.product_sku}</Text>
          <Tag>{record.product_barcode}</Tag>
          {(record.batch || record.expiration_date) && (
            <Space size="small" style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              {record.batch && <span>Batch: {record.batch}</span>}
              {record.expiration_date && <span>Exp: {record.expiration_date}</span>}
            </Space>
          )}
          <CustomAttributesTags
            attributes={record.custom_attributes}
            attributeLabelByName={attributeLabelByName}
            emptyFallback={null}
          />
        </Space>
      ),
    },
    {
      title: t('operator.inbound_item.quantity', { defaultValue: '數量' }),
      key: 'quantity',
      width: 200,
      render: (_, record) => {
        const isReceived = !!record.received_at;
        const received = record.received_quantity || 0;
        const scheduled = record.scheduled_quantity || 0;
        const percent = scheduled > 0 ? Math.min(100, (received / scheduled) * 100) : 0;

        let status: 'active' | 'normal' | 'success' | 'exception' = 'active';
        let strokeColor: string | undefined = undefined;
        let textColor = '#999';
        let receivedDisplay: number | string = isReceived ? received : '-';

        if (!isReceived) {
          status = 'normal';
          textColor = '#999';
        } else if (received > scheduled) {
          status = 'active';
          strokeColor = '#faad14';
          textColor = '#faad14';
        } else if (received === scheduled) {
          status = 'success';
          textColor = '#52c41a';
        } else {
          status = 'exception';
          strokeColor = '#ff4d4f';
          textColor = '#ff4d4f';
        }

        return (
          <div style={{ width: '100%', textAlign: 'right' }}>
            <div style={{ marginBottom: 4 }}>
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: textColor
              }}>
                {receivedDisplay}
              </span>
              <span style={{ color: '#999', margin: '0 4px' }}>/</span>
              <span style={{ fontSize: '14px', color: '#666' }}>{scheduled}</span>
              {isReceived && (
                <Tooltip title={record.received_at}>
                  <Tag color={received >= scheduled ? 'success' : 'warning'} style={{ marginLeft: 8, fontSize: '10px' }}>
                    {t('operator.inbound_item.received', { defaultValue: '已驗' })}
                  </Tag>
                </Tooltip>
              )}
              {(record.receipt_items?.length || 0) > 1 && (
                <Badge count={t('models.receipt_item.batch_count', { count: record.receipt_items!.length })} style={{ backgroundColor: '#1890ff', marginLeft: 4 }} />
              )}
            </div>

            <Progress
              percent={isReceived ? percent : 0}
              size="small"
              status={status}
              showInfo={false}
              strokeColor={strokeColor}
              style={{ marginBottom: 4 }}
            />

          </div>
        );
      },
    }, {
      title: t('common.actions', { defaultValue: '操作' }),
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          icon={<FormOutlined />}
          onClick={() => handleManualVerify(record)}
        >
          {t('common.button.verify', { defaultValue: '驗收' })}
        </Button>
      ),
    },
  ];

  return (
    <div className="inbound-items-table" style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
      <Table
        dataSource={(inbound?.items || []) as InboundItemRecord[]}
        columns={columns}
        rowKey="id"
        pagination={false}
        rowClassName={(record) => record.id === scannedItem?.id ? 'highlight-row' : ''}
        onRow={(record) => ({
          style: record.id === scannedItem?.id ? { backgroundColor: '#e6f7ff' } : {}
        })}
      />
    </div>
  );
};

export default ItemsTable;
